import { useAtomValue } from "jotai";
import { useRef, ReactNode } from "react";
import { Whisper, Popover, Tooltip } from "rsuite";
import { OverlayTriggerType } from "rsuite/esm/internals/Overlay/OverlayTrigger";
import { IsMobileAtom } from "../App";
import { TypeAttributes } from "rsuite/esm/internals/types";
/**
 * App Tooltip Component
 *
 * If you want to gain access to the Whisper's ref, use contentIsFunction or childrenIsFunction props,
 * and pass the ref to the function.
 *
 * To change popover style: className="sidebar-popover" full
 *
 * @param {Object} content - Content to display in the tooltip.
 * @param {Object} children - Hoverable element to display the tooltip on.
 * @param {boolean} useTooltip - Use the default Rsuite tooltip (simple black tooltip)
 * @param {boolean} useSpan -Use a span instead of a div - Useful for formatting is certain cases
 */

interface AppTooltipProps {
  placement?: TypeAttributes.Placement;
  trigger?: OverlayTriggerType;
  children: any;
  content?: object | ReactNode | null;
  useTooltip?: boolean;
  disabled?: boolean;
  enterable?: boolean;
  useSpan?: boolean;
  delayOpen?: number;
  delayClose?: number;
  externalRef?: any;
  onEntering?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  className?: string;
}

const AppTooltip = ({
  children,
  trigger,
  content = null,
  disabled = false,
  placement = "autoHorizontal",
  enterable = true,
  delayOpen = 400,
  delayClose = 0,
  externalRef = null,
  useTooltip = false,
  useSpan = false,
  onEntering,
  onOpen,
  onClose,
  className,
}: AppTooltipProps) => {
  const mobileScreen = useAtomValue(IsMobileAtom);
  const tooltipRef = useRef();

  return (
    <Whisper
      placement={placement}
      trigger={trigger ? trigger : mobileScreen ? "none" : "hover"}
      onOpen={onOpen}
      onClose={onClose}
      disabled={disabled}
      enterable={enterable}
      delayOpen={delayOpen}
      delayClose={delayClose}
      onEntering={onEntering}
      ref={externalRef ? externalRef : tooltipRef}
      preventOverflow
      speaker={
        useTooltip ? (
          <Tooltip>
            {typeof content === "function"
              ? content(externalRef ? externalRef : tooltipRef)
              : content}
          </Tooltip>
        ) : (
          <Popover className="p-0">
            {typeof content === "function"
              ? content(externalRef ? externalRef : tooltipRef)
              : content}
          </Popover>
        )
      }
    >
      {useSpan ? (
        <span>
          {typeof children === "function"
            ? children(externalRef ? externalRef : tooltipRef)
            : children}
        </span>
      ) : (
        <div className={className}>
          {typeof children === "function"
            ? children(externalRef ? externalRef : tooltipRef)
            : children}
        </div>
      )}
    </Whisper>
  );
};

export default AppTooltip;
